import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { tierDisplayName, type Tier } from "@/lib/tier";

type CompUserRecord = Pick<
  Tables<"profiles">,
  "id" | "owner_user_id" | "display_name" | "workspace_label" | "account_tier" | "created_at"
>;

export function CompAccountsTab() {
  const [compEmail, setCompEmail] = useState("");
  const [compName, setCompName] = useState("");
  const [compBusy, setCompBusy] = useState(false);
  const [compUsers, setCompUsers] = useState<CompUserRecord[]>([]);
  const [revokeBusyId, setRevokeBusyId] = useState<string | null>(null);

  const loadCompUsers = async () => {
    const { data } = await supabase
      .from("profiles")
      .select("id, owner_user_id, display_name, workspace_label, account_tier, created_at")
      .eq("account_tier", "comp")
      .eq("is_primary", true)
      .order("created_at", { ascending: false });
    setCompUsers(data ?? []);
  };

  useEffect(() => { void loadCompUsers(); }, []);

  const createCompAccount = async () => {
    const email = compEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Enter a valid email");
    setCompBusy(true);
    const { data, error } = await supabase.functions.invoke("admin-create-comp-user", {
      body: { email, display_name: compName.trim() },
    });
    setCompBusy(false);
    if (error) return toast.error(error.message);
    if (data?.error) return toast.error(data.error);
    toast.success(
      data?.action === "upgrade_to_comp"
        ? "Existing account upgraded to comp"
        : "Invite sent — they'll receive an email to set a password",
    );
    setCompEmail(""); setCompName("");
    await loadCompUsers();
  };

  const revokeComp = async (userId: string, label: string, mode: "downgrade" | "delete") => {
    const msg = mode === "delete"
      ? `Permanently delete "${label}" and ALL their data? This cannot be undone.`
      : `Downgrade "${label}" to a free account? They keep their data and login.`;
    if (!confirm(msg)) return;
    setRevokeBusyId(userId);
    const { data, error } = await supabase.functions.invoke("admin-revoke-comp-user", {
      body: { user_id: userId, mode },
    });
    setRevokeBusyId(null);
    if (error) return toast.error(error.message);
    if (data?.error) return toast.error(data.error);
    toast.success(mode === "delete" ? "Account deleted" : "Downgraded to free");
    await loadCompUsers();
  };

  return (
    <div className="space-y-4">
      <Card className="shadow-soft">
        <CardHeader><CardTitle>Create a comp account</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Provision a free account for someone else. They'll get an email to set their password and land in their own workspace.
            If the email already has an account, it will be upgraded to comp tier instead.
          </p>
          <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <Input type="email" placeholder="email@example.com" value={compEmail} onChange={(e) => setCompEmail(e.target.value)} disabled={compBusy} />
            <Input placeholder="Display name (optional)" value={compName} onChange={(e) => setCompName(e.target.value)} disabled={compBusy} maxLength={100} />
            <Button onClick={createCompAccount} disabled={compBusy}>{compBusy ? "Sending…" : "Create & invite"}</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-soft">
        <CardHeader><CardTitle>Comp accounts</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {compUsers.length === 0 && <p className="py-4 text-sm text-muted-foreground">No comp accounts yet.</p>}
          {compUsers.map((u) => (
            <div key={u.id} className="flex flex-col gap-3 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col">
                <span className="font-medium">{u.workspace_label || u.display_name || "Untitled"}</span>
                <span className="text-xs text-muted-foreground">Created {new Date(u.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                <Badge variant="secondary">{tierDisplayName(u.account_tier as Tier)}</Badge>
                <Button variant="ghost" size="sm" disabled={revokeBusyId === u.owner_user_id} onClick={() => revokeComp(u.owner_user_id, u.workspace_label || u.display_name || "this account", "downgrade")}>Downgrade</Button>
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" disabled={revokeBusyId === u.owner_user_id} onClick={() => revokeComp(u.owner_user_id, u.workspace_label || u.display_name || "this account", "delete")}>Delete</Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
